import { useCallback, useEffect, useRef } from 'react';
import { APP_INSIGHTS_EVENT_TYPE } from 'common/utils/telemetry';
import { useAppInsights } from './useAppInsights';

export type CaptureDeeplinkStage =
  | 'route-entered'
  | 'token-requested'
  | 'token-acquired'
  | 'token-failed'
  | 'app-config-loading'
  | 'app-config-ready'
  | 'app-config-failed'
  | 'optional-preferences-bypassed'
  | 'bootstrap-timeout'
  | 'profile-request-loading'
  | 'profile-request-timeout'
  | 'profile-request-ready'
  | 'profile-request-failed'
  | 'invalid-source-profile'
  | 'profile-unmatched'
  | 'project-redirect'
  | 'project-selection-required'
  | 'team-selection-required'
  | 'team-selected'
  | 'profile-selected'
  | 'capture-ready';

const CAPTURE_DEEPLINK_TRACE_ID_KEY = 'captureDeeplinkTraceId';

const createFallbackTraceId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `capture-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export const getCaptureDeeplinkTraceId = () => {
  if (typeof sessionStorage === 'undefined') return createFallbackTraceId();

  // Route bootstrap and profile lookup live in separate components. Session
  // storage gives both the same ID without extending it beyond this browser tab.
  const existingTraceId = sessionStorage.getItem(CAPTURE_DEEPLINK_TRACE_ID_KEY);
  if (existingTraceId) return existingTraceId;

  const traceId = createFallbackTraceId();
  sessionStorage.setItem(CAPTURE_DEEPLINK_TRACE_ID_KEY, traceId);
  return traceId;
};

export const useCaptureDeeplinkTrace = () => {
  const { trackEvent } = useAppInsights();
  const trackEventRef = useRef(trackEvent);
  const traceId = useRef<string | null>(null);
  const completedStages = useRef(new Set<CaptureDeeplinkStage>());

  useEffect(() => {
    trackEventRef.current = trackEvent;
  }, [trackEvent]);

  return useCallback(
    (
      stage: CaptureDeeplinkStage,
      properties: Record<string, unknown> = {},
      { once = true }: { once?: boolean } = {}
    ) => {
      const activeTraceId = getCaptureDeeplinkTraceId();
      // AuthorizedRoute survives warm SPA navigation. Reset its in-memory stage
      // de-duplication when capture-ready has cleared storage and a new attempt begins.
      if (traceId.current !== activeTraceId) {
        traceId.current = activeTraceId;
        completedStages.current.clear();
      }

      if (once && completedStages.current.has(stage)) return;
      completedStages.current.add(stage);

      trackEventRef.current({
        type:
          stage.endsWith('failed') ||
          stage === 'bootstrap-timeout' ||
          stage === 'profile-request-timeout' ||
          stage === 'invalid-source-profile'
            ? APP_INSIGHTS_EVENT_TYPE.ERROR
            : APP_INSIGHTS_EVENT_TYPE.TRACE,
        name: `Capture Deeplink - ${stage}`,
        properties: {
          captureDeeplinkTraceId: traceId.current,
          captureDeeplinkStage: stage,
          ...properties,
        },
      });

      // End the correlation window only after Capture can render. The next
      // deeplink in this tab will then allocate a new trace ID.
      if (stage === 'capture-ready' && typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(CAPTURE_DEEPLINK_TRACE_ID_KEY);
      }
    },
    []
  );
};
